<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounce } from '@vueuse/core';
import type { FieldSchema } from '../../../types';

export interface Model {
  amount: string;
  amount_unit: string;
}

defineProps<{
  disabled?: boolean;
  error?: string;
  field?: FieldSchema;
  placeholder?: string;
  required?: boolean;
}>();

const { t } = useI18n();

const model = defineModel<Model | undefined>();
const modelNumber = ref();
const modelUnit = ref();

const options = computed(() => [
  `${t('units.application')}`,
  `${t('units.capsule')}`,
  `${t('units.cm')}`,
  `${t('units.cup')}`,
  `${t('units.dose')}`,
  `${t('units.drop(s)')}`,
  `${t('units.g')}`,
  `${t('units.g/kg')}`,
  `${t('units.gr/kg')}`,
  `${t('units.l/kg')}`,
  `${t('units.mcg')}`,
  `${t('units.mcgs')}`,
  `${t('units.mcgs/kg')}`,
  `${t('units.mg')}`,
  `${t('units.mg/kg')}`,
  `${t('units.mg/lb')}`,
  `${t('units.ml')}`,
  `${t('units.ml/hr')}`,
  `${t('units.ml/kg')}`,
  `${t('units.ml/lb')}`,
  `${t('units.oz')}`,
  `${t('units.package')}`,
  `${t('units.pinch')}`,
  `${t('units.pipette')}`,
  `${t('units.scoop')}`,
  `${t('units.spray')}`,
  `${t('units.tablet')}`,
  `${t('units.tablet/kg')}`,
  `${t('units.tblsp')}`,
  `${t('units.tsp')}`,
  `${t('units.unit')}`,
  `${t('units.units/kg')}`,
]);

const input = useDebounce(modelNumber, 400);

onMounted(() => setModelsOnStart());

watch(input, () => numberInputRulesHandler());
watch([input, modelUnit], () => updateModel());
watch(model, setModelsOnStart);

function numberInputRulesHandler() {
  let value = modelNumber.value?.toString() || null;

  modelNumber.value = value;
}

function setModelsOnStart() {
  if (!model.value) {
    modelNumber.value = null;
    modelUnit.value = null;
    model.value = undefined;
    return;
  }

  modelNumber.value = model.value.amount || null;
  modelUnit.value = model.value?.amount_unit?.toLowerCase();
  numberInputRulesHandler();
}

function updateModel() {
  model.value = { amount: modelNumber.value, amount_unit: modelUnit.value };
}
</script>

<template>
  <div class="flex flex-col gap-3 sm:flex-row">
    <div class="w-full sm:!w-1/2">
      <AFieldInputText
        v-model="modelNumber"
        :error
        :required
        :disabled="field?.state?.disabled || disabled"
        :showButtons="false"
      />
    </div>

    <AFieldDropdown
      v-model="modelUnit"
      class="w-full sm:!w-1/2"
      :error
      :options
      :placeholder
      :required
      :disabled="field?.state?.disabled || disabled"
      :showClear="!required"
      @change="updateModel"
    />
  </div>
</template>
